'use client';

import { createStore } from 'zustand/vanilla';
import { gqlClient, graphqlRequest } from '@/api/amplify/helpers';
import { projectsByTypeKey } from '@/graphql/queries';
import { createProject as createProjectRequest } from '@/graphql/mutations';
import { project, publishMethodOptions } from '@/types/entities';
import { useUserDataStore } from '@/stores/userDataStore';
import { customAlphabet } from 'nanoid';

export type projectState = {
  projects: project[];
  isLoading: boolean;
};

type fetchByTypeKeyProps = {
  appKey: string;
  set: (state: Partial<projectState>) => void;
  get: () => projectState;
  appUrl: string;
  owner: string;
};

const getProjectTypeKey = (appKey) => {
  return `${appKey}_${publishMethodOptions.studio}`;
};

const createPublishId = () => {
  const nanoid = customAlphabet('0123456789abcdefghijklmnopqrstuvwxyz', 13);
  return nanoid();
};

const createProject = async ({ appKey, appUrl, owner }) => {
  const input = {
    name: 'Studio Project',
    description: '',
    verticalOrientation: true,
    chatLandingPage: false,
    collectAfterAnyResponse: true,
    dynamic: true,
    targetPage: 'studio',
    discover: false,
    useCaseName: publishMethodOptions.studio,
    appUrl,
    feed: true,
    tolstoyType: publishMethodOptions.studio,
    owner,
    publishMethod: publishMethodOptions.studio,
    publishId: createPublishId(),
    appKey,
    typeKey: getProjectTypeKey(appKey),
    live: true,
  };

  const project = await graphqlRequest({
    query: createProjectRequest,
    variables: {
      input,
    },
  });

  return project;
};

const fetchByTypeKey = async ({ appKey, set, get, owner, appUrl }: fetchByTypeKeyProps) => {
  const { isLoading } = get();
  if (isLoading) {
    return;
  }

  set({
    isLoading: true,
  });

  try {
    const typeKey = getProjectTypeKey(appKey);
    const projectsPromise = await graphqlRequest({
      query: projectsByTypeKey,
      variables: {
        typeKey,
        appKey,
      },
    });

    const projects = projectsPromise.data.projectsByTypeKey.items;

    if (!projects.length && owner) {
      await createProject({ appKey, owner, appUrl });
    }

    set({
      projects,
    });
  } catch (err) {
    console.log(err);
  }

  set({
    isLoading: false,
  });
};

export const defaultInitState: projectState = {
  projects: [] as project[],
  isLoading: false,
};

export type projectStore = projectState & {
  fetchByTypeKey: () => Promise<void>;
};

export const createProjectStore = () => {
  const { appKey, appUrl, owner } = useUserDataStore.getState();

  const store = createStore<projectStore>()((set, get) => ({
    ...defaultInitState,
    fetchByTypeKey: () => fetchByTypeKey({ owner, appUrl, appKey, set, get }),
  }));

  return store;
};
